/**
 * O convite para colocar a barbearia na tela de início.
 *
 * Fica embaixo do login, não por cima: quem abre este endereço quase sempre é
 * cliente da casa querendo ver o próprio horário, e uma barra de instalação
 * cobrindo o formulário atrasa exatamente o que a pessoa veio fazer.
 *
 * A forma muda com o que o navegador permite, e não por gosto (ver
 * `lib/install.ts`):
 *
 *  - No **Chromium** existe diálogo de verdade, então existe botão.
 *  - No **iPhone** não existe. Um botão que só abre um passo a passo é um toque
 *    cobrado para entregar uma frase — a frase fica na tela, direto, com o
 *    ícone que a pessoa vai procurar na barra do Safari.
 *  - No **navegador embutido do Instagram** não dá para instalar de jeito
 *    nenhum; a única instrução honesta é como sair dele.
 *
 * Sem botão de fechar, de propósito. Discreto e fora do caminho, ele não pede
 * para ser dispensado — e um "não agora" que valesse para sempre tiraria o
 * convite de quem só estava com pressa naquele dia. Quem instala para de vê-lo
 * pela porta certa: passa a abrir pelo app.
 */

import { useInstallPrompt } from '../lib/install.ts';
import { Button } from './ui.tsx';

export function InstallPrompt() {
  const { status, install } = useInstallPrompt();

  if (status === 'hidden') return null;

  if (status === 'prompt') {
    return (
      <div className="mb-8">
        <Button variant="secondary" full onClick={() => void install()}>
          <Phone />
          Instalar aplicativo
        </Button>
      </div>
    );
  }

  return (
    <p className="mb-8 rounded-xl border border-line bg-surface-raised px-3 py-2.5 text-[13px] leading-relaxed text-ink-soft">
      {status === 'ios' ? (
        <>
          <Phone /> No iPhone: toque em <Strong>Compartilhar</Strong> (o ícone <Share /> da barra do
          Safari) e depois em <Strong>Adicionar à Tela de Início</Strong>.
        </>
      ) : (
        <>
          <Phone /> Para instalar na tela de início, toque em <Strong>⋯</Strong> e escolha{' '}
          <Strong>Abrir no navegador</Strong>.
        </>
      )}
    </p>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return <strong className="font-semibold text-ink">{children}</strong>;
}

/** Um celular, para a frase começar dizendo do que ela trata. */
function Phone() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="mr-0.5 mb-0.5 inline-block h-[15px] w-[15px] align-middle text-accent"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="celular"
    >
      <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
      <path d="M10.5 18.5h3" />
    </svg>
  );
}

/** O quadrado com a seta para cima do iOS, no tamanho da linha de texto. */
function Share() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="mb-0.5 inline-block h-[17px] w-[17px] align-middle text-accent"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label="ícone de compartilhar"
    >
      <path d="M8.5 10.5H6.5a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-8a1 1 0 0 0-1-1h-2" />
      <path d="M12 14.5V3.5" />
      <path d="M8.5 6.5 12 3l3.5 3.5" />
    </svg>
  );
}
